import { useDragSelectionContextOptional } from "@/contexts/DragSelectionContext";
import { MAX_OOO_HOURS } from "@/utils/oooConstants";
import type { ProjectAllocation } from "@/utils/makersUtils";
import { getProjectFillStyle } from "@/utils/projectColor";
import { buildTimelineCells } from "@/utils/projectUtils";
import { type WeekColumn } from "@/utils/weekUtils";
import { getStartOfWeek } from "@/utils/weekUtils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface TimelineRowProps {
  className: string;
  totalWeeklyHours: number;
  weeks: WeekColumn[];
  projectAllocations: ProjectAllocation[];
  onHoursChange?: (weekKey: number, newHours: number, singleCellOnly: boolean) => void;
  editable?: boolean;
  projectStartDate?: string;
  projectEndDate?: string;
  /** Unique ID for this row, used for bulk selection across rows */
  rowId?: string;
  /** Whether to use shared drag selection context (enables cross-row selection) */
  useSharedSelection?: boolean;
  /**
   * Out-of-office (OOO) hours keyed by week key (week.start.getTime()). Rendered
   * as a grey overlay box. Visual only — never affects allocation totals.
   */
  oooByWeekKey?: Map<number, number>;
  /** When true, clicking/dragging cells lets the user set OOO hours (Makers board). */
  oooEditable?: boolean;
  /** Persist an OOO change for one or more weeks (hours <= 0 clears them). */
  onOooChange?: (weekKeys: number[], hours: number) => void;
  /**
   * Label for the allocated-hours line of the hover tooltip. On the Makers board
   * a cell is the maker's whole allocation ("Total"); on a project's timeline it
   * is only that project's hours ("This project"), which would read as a
   * whole-week number next to the maker's OOO if it were labelled the same.
   */
  hoursLabel?: string;
}

/**
 * The single hover tooltip for a cell. Allocated hours and OOO used to live in
 * two states rendered by an if/else, so a cell with OOO never showed its hours.
 * One state carrying both means the two can't compete.
 */
interface CellTooltipState {
  /** Allocated hours; 0 when the cell has no allocation (or is being edited). */
  totalHours: number;
  /** Out-of-office hours; 0 when the cell has none. */
  oooHours: number;
  x: number;
  y: number;
}

const CELL_WIDTH_PX = 64;

interface ProjectFill {
  projectId: string | null;
  heightPercentage: number;
  bottomPercentage: number;
  unassigned: boolean;
}

interface TimelineCellModel {
  week: WeekColumn;
  key: number;
  hasAllocation: boolean;
  totalHours: number;
  fills: ProjectFill[];
}

/**
 * The cell hover tooltip. A cell with no OOO keeps the compact single-number
 * chip; once OOO is involved both numbers are labelled, because "44h" and "16h"
 * stacked without labels is worse than either one alone. Zero rows are dropped
 * rather than printed as "0h".
 */
function renderCellTooltip(tooltip: CellTooltipState, hoursLabel: string) {
  const { totalHours, oooHours, x, y } = tooltip;
  const position = { left: x, top: y - 8 };

  if (oooHours <= 0) {
    return createPortal(
      <div className="hours-tooltip" style={position} role="tooltip">
        {totalHours}h
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="ooo-tooltip" style={position} role="tooltip">
      {totalHours > 0 ? (
        <span className="ooo-tooltip__row">
          <span className="ooo-tooltip__label">{hoursLabel}</span>
          <span className="ooo-tooltip__hours">{totalHours}h</span>
        </span>
      ) : null}
      <span className="ooo-tooltip__row">
        <span className="ooo-tooltip__label">Out of office</span>
        <span className="ooo-tooltip__hours">{oooHours}h</span>
      </span>
    </div>,
    document.body,
  );
}

/**
 * Timeline row component displaying week allocations.
 * Supports both single-cell editing and bulk selection via drag.
 *
 * When useSharedSelection=true and rowId is provided, this row integrates
 * with the DragSelectionContext to enable cross-row bulk selection.
 */
export function TimelineRow({
  weeks,
  className,
  totalWeeklyHours: weeklyCapacityHours,
  projectAllocations = [],
  onHoursChange,
  editable = false,
  projectStartDate,
  projectEndDate,
  rowId,
  useSharedSelection = false,
  oooByWeekKey,
  oooEditable = false,
  onOooChange,
  hoursLabel = "Total",
}: TimelineRowProps) {
  const [editingWeek, setEditingWeek] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [singleCellEdit, setSingleCellEdit] = useState(false);
  const [editingOooWeek, setEditingOooWeek] = useState<number | null>(null);
  const [oooValue, setOooValue] = useState<string>("");
  const [oooSelection, setOooSelection] = useState<Set<number>>(new Set());
  // Allocated hours are no longer drawn inside the cell (they'd be unreadable on
  // dark fills); they surface in this hover tooltip and in the edit input.
  const [cellTooltip, setCellTooltip] = useState<CellTooltipState | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Drag state for OOO multi-select. Refs avoid re-render churn mid-drag.
  const oooDragging = useRef(false);
  const oooAnchorIndex = useRef<number | null>(null);
  const oooLastIndex = useRef<number | null>(null);
  // Week keys the committed OOO value should apply to (set on drag release).
  const oooApplyKeys = useRef<number[]>([]);
  // Flips on drag start/end to (de)activate the window listeners that resolve
  // the drag even when the pointer is released outside the grid.
  const [oooDragActive, setOooDragActive] = useState(false);

  // Get drag selection context if using shared selection
  const dragSelection = useDragSelectionContextOptional();
  const shouldUseSharedSelection = useSharedSelection && rowId && dragSelection;

  const cells = useMemo<TimelineCellModel[]>(() => {
    return buildTimelineCells(weeks, projectAllocations, weeklyCapacityHours);
  }, [weeks, projectAllocations, weeklyCapacityHours]);

  // Effective custom color per project (null = fall back to the id-derived
  // default), so a project the user recolored renders with its stored color.
  const projectColorById = useMemo(() => {
    const map = new Map<string | null, string | null>();
    for (const pa of projectAllocations) {
      map.set(pa.projectId, pa.project?.color ?? null);
    }
    return map;
  }, [projectAllocations]);

  // Identify projects with any unconfirmed assignment (for stripe styling)
  const unconfirmedProjectIds = useMemo(() => {
    const ids = new Set<string | null>();
    for (const pa of projectAllocations) {
      if (pa.assignments.some(a => !a.is_confirmed)) {
        ids.add(pa.projectId);
      }
    }
    return ids;
  }, [projectAllocations]);

  // Register this row's grid with the drag selection context
  const cellKeys = useMemo(() => cells.map(c => c.key), [cells]);

  useEffect(() => {
    if (shouldUseSharedSelection && gridRef.current) {
      dragSelection.registerRowGrid(rowId, gridRef.current, cellKeys);
      return () => {
        dragSelection.registerRowGrid(rowId, null, []);
      };
    }
  }, [shouldUseSharedSelection, rowId, dragSelection, cellKeys]);

  // Watch for single cell clicks from the drag selection context
  useEffect(() => {
    if (shouldUseSharedSelection && dragSelection.lastSingleCellClick) {
      const { rowId: clickedRowId, cellKey, singleCellOnly } = dragSelection.lastSingleCellClick;
      if (clickedRowId === rowId) {
        // Find the cell to get its current value
        const cell = cells.find(c => c.key === cellKey);
        if (cell) {
          setSingleCellEdit(singleCellOnly);
          setEditingWeek(cellKey);
          setEditingValue(cell.totalHours.toString());
          dragSelection.clearLastSingleCellClick();
        }
      }
    }
  }, [shouldUseSharedSelection, dragSelection, rowId, cells]);

  const isWeekBeforeProjectStart = useCallback((week: WeekColumn): boolean => {
    if (!projectStartDate) return false;
    const projectStart = new Date(projectStartDate);
    const projectStartWeek = getStartOfWeek(projectStart);
    const weekStart = getStartOfWeek(week.start);
    return weekStart.getTime() < projectStartWeek.getTime();
  }, [projectStartDate]);

  const isWeekAfterProjectEnd = useCallback((week: WeekColumn): boolean => {
    if (!projectEndDate) return false;
    const projectEnd = new Date(projectEndDate);
    const projectEndWeek = getStartOfWeek(projectEnd);
    const weekStart = getStartOfWeek(week.start);
    return weekStart.getTime() > projectEndWeek.getTime();
  }, [projectEndDate]);

  const isOutsideProjectRange = useCallback((week: WeekColumn): boolean => {
    return isWeekBeforeProjectStart(week) || isWeekAfterProjectEnd(week);
  }, [isWeekBeforeProjectStart, isWeekAfterProjectEnd]);

  const handleCellPointerDown = useCallback((
    e: React.PointerEvent,
    cell: TimelineCellModel
  ) => {
    // Ignore clicks on the input element (e.g., number spinner arrows)
    if ((e.target as HTMLElement).tagName === "INPUT") return;

    if (!editable || isOutsideProjectRange(cell.week)) return;

    if (shouldUseSharedSelection) {
      // Use shared drag selection
      dragSelection.handlePointerDown(e, rowId, cell.key);
    } else {
      // Single cell edit mode when Cmd/Ctrl is held
      setSingleCellEdit(e.metaKey || e.ctrlKey);
      setEditingWeek(cell.key);
      setEditingValue(cell.totalHours.toString());
    }
  }, [editable, isOutsideProjectRange, shouldUseSharedSelection, dragSelection, rowId]);

  // ---- OOO drag-to-select editing (Makers board) --------------------------

  /** Map a pointer X position to a cell index within this row's grid. */
  const indexFromClientX = useCallback((clientX: number): number | null => {
    const grid = gridRef.current;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    const x = clientX - rect.left + grid.scrollLeft;
    // Columns are laid out with a gap between them (see .timeline-grid), so the
    // per-column pitch is the cell width PLUS that gap. Dividing by the bare cell
    // width would drift by one gap per column, mis-targeting weeks as the
    // timeline grows. Read the rendered gap so the math tracks the layout.
    const columnGap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    const pitch = CELL_WIDTH_PX + columnGap;
    let idx = Math.floor(x / pitch);
    if (idx < 0) idx = 0;
    if (idx > cells.length - 1) idx = cells.length - 1;
    return idx;
  }, [cells.length]);

  const rangeKeys = useCallback((a: number, b: number): number[] => {
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    const keys: number[] = [];
    for (let i = lo; i <= hi; i++) {
      const cell = cells[i];
      if (cell) keys.push(cell.key);
    }
    return keys;
  }, [cells]);

  const releaseOooCapture = useCallback((pointerId?: number) => {
    if (pointerId === undefined) return;
    try {
      gridRef.current?.releasePointerCapture(pointerId);
    } catch {
      // ignore — capture may never have been acquired.
    }
  }, []);

  const handleOooPointerDown = useCallback((e: React.PointerEvent, cell: TimelineCellModel) => {
    if ((e.target as HTMLElement).tagName === "INPUT") return;
    const idx = cellKeys.indexOf(cell.key);
    if (idx === -1) return;
    oooDragging.current = true;
    oooAnchorIndex.current = idx;
    oooLastIndex.current = idx;
    setOooSelection(new Set([cell.key]));
    setCellTooltip(null);
    setOooDragActive(true);
    try {
      gridRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture can throw if the pointer is already released; the
      // window listeners below still resolve the drag, so ignore.
    }
  }, [cellKeys]);

  const updateOooDrag = useCallback((clientX: number) => {
    if (!oooDragging.current || oooAnchorIndex.current === null) return;
    const idx = indexFromClientX(clientX);
    if (idx === null) return;
    oooLastIndex.current = idx;
    setOooSelection(new Set(rangeKeys(oooAnchorIndex.current, idx)));
  }, [indexFromClientX, rangeKeys]);

  // Finish a drag: stop dragging and open the editor on the anchor cell. The
  // value entered there applies to the whole selected group on commit.
  const finishOooDrag = useCallback((pointerId?: number) => {
    if (!oooDragging.current) return;
    oooDragging.current = false;
    setOooDragActive(false);
    releaseOooCapture(pointerId);
    const anchorIdx = oooAnchorIndex.current;
    if (anchorIdx === null) return;
    const lastIdx = oooLastIndex.current ?? anchorIdx;
    const keys = rangeKeys(anchorIdx, lastIdx);
    oooApplyKeys.current = keys;
    setOooSelection(new Set(keys));
    const anchorKey = cells[anchorIdx]?.key;
    if (anchorKey === undefined) return;
    const existing = oooByWeekKey?.get(anchorKey) ?? 0;
    setEditingOooWeek(anchorKey);
    setOooValue(existing > 0 ? String(existing) : "");
  }, [rangeKeys, cells, oooByWeekKey, releaseOooCapture]);

  // Abort a drag without opening the editor (e.g. the gesture was canceled).
  const abortOooDrag = useCallback((pointerId?: number) => {
    if (!oooDragging.current) return;
    oooDragging.current = false;
    setOooDragActive(false);
    releaseOooCapture(pointerId);
    oooAnchorIndex.current = null;
    oooLastIndex.current = null;
    oooApplyKeys.current = [];
    setOooSelection(new Set());
  }, [releaseOooCapture]);

  // While a drag is in progress, resolve it from the window so it never gets
  // stuck — covers releases outside the grid, failed pointer capture, and
  // canceled gestures, none of which would reach the grid's own handlers.
  useEffect(() => {
    if (!oooDragActive) return;
    const onMove = (e: PointerEvent) => updateOooDrag(e.clientX);
    const onUp = (e: PointerEvent) => finishOooDrag(e.pointerId);
    const onCancel = (e: PointerEvent) => abortOooDrag(e.pointerId);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [oooDragActive, updateOooDrag, finishOooDrag, abortOooDrag]);

  const commitOoo = useCallback(() => {
    const trimmed = oooValue.trim();
    const numValue = trimmed === "" ? 0 : parseFloat(trimmed);
    const targets = oooApplyKeys.current.length > 0
      ? oooApplyKeys.current
      : editingOooWeek !== null ? [editingOooWeek] : [];
    if (!isNaN(numValue) && numValue >= 0 && targets.length > 0) {
      // Commit the whole selection at once so a range logs as a single audit entry.
      onOooChange?.(targets, numValue);
    }
    setEditingOooWeek(null);
    setOooValue("");
    setOooSelection(new Set());
    oooApplyKeys.current = [];
  }, [oooValue, onOooChange, editingOooWeek]);

  const cancelOoo = useCallback(() => {
    setEditingOooWeek(null);
    setOooValue("");
    setOooSelection(new Set());
    oooApplyKeys.current = [];
  }, []);

  const showCellTooltip = useCallback(
    (e: React.MouseEvent, totalHours: number, oooHours: number) => {
      // A tooltip chasing the pointer across every swept cell is noise, so the
      // whole thing stays hidden while OOO is being edited or drag-selected.
      if (oooDragging.current || editingOooWeek !== null) return;
      if (totalHours <= 0 && oooHours <= 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setCellTooltip({ totalHours, oooHours, x: rect.left + rect.width / 2, y: rect.top });
    },
    [editingOooWeek],
  );

  // Hide the hover tooltip once a cell enters edit mode (the input shows the
  // value); the pointer stays over the cell so no mouseleave fires on its own.
  useEffect(() => {
    if (editingWeek !== null) setCellTooltip(null);
  }, [editingWeek]);

  const isCellSelected = useCallback((cellKey: number): boolean => {
    if (!shouldUseSharedSelection) return false;
    return dragSelection.isSelected(rowId, cellKey);
  }, [shouldUseSharedSelection, dragSelection, rowId]);

  // Don't show local edit UI when bulk selecting
  const showLocalEdit = !shouldUseSharedSelection || !dragSelection.showBulkInput;

  return (
    <div className={`employee-row__timeline-content ${className}`}>
      <div
        ref={gridRef}
        className="timeline-grid"
        style={{ gridTemplateColumns: `repeat(${weeks.length}, ${CELL_WIDTH_PX}px)` }}
      >
        {cells.map((cell) => {
          const isSelected = isCellSelected(cell.key);
          const isOutside = isOutsideProjectRange(cell.week);
          const canEdit = editable && !isOutside;
          const oooHours = oooByWeekKey?.get(cell.key) ?? 0;
          const isEditingOoo = oooEditable && editingOooWeek === cell.key;
          const isOooSelected = oooEditable && oooSelection.has(cell.key);
          const oooHeight =
            weeklyCapacityHours > 0
              ? Math.min(100, Math.max(12, (oooHours / weeklyCapacityHours) * 100))
              : 100;
          const hasOoo = oooHours > 0 && !isEditingOoo;

          return (
            <div
              key={cell.key}
              className={`timeline-cell timeline-cell--${cell.hasAllocation ? "active" : "empty"} ${isSelected ? "timeline-cell--selected" : ""} ${canEdit ? "timeline-cell--editable" : ""} ${oooEditable ? "timeline-cell--ooo-editable" : ""} ${isOooSelected ? "timeline-cell--ooo-selected" : ""} ${isOutside ? "timeline-cell--disabled" : ""} ${canEdit && editingWeek === cell.key && singleCellEdit ? "timeline-cell--single-edit" : ""}`}
              data-testid="timeline-cell"
              data-week-key={cell.key}
              data-hours={cell.totalHours}
              data-has-allocation={cell.hasAllocation}
              data-ooo-hours={oooHours}
              data-outside={isOutside}
              data-editable={canEdit}
              onPointerDown={(e) =>
                oooEditable ? handleOooPointerDown(e, cell) : handleCellPointerDown(e, cell)
              }
              onMouseEnter={(e) =>
                showCellTooltip(
                  e,
                  cell.hasAllocation && editingWeek !== cell.key ? cell.totalHours : 0,
                  hasOoo ? oooHours : 0,
                )
              }
              onMouseLeave={() => setCellTooltip(null)}
            >
              {cell.fills.map((fill) => {
                const colorStyle = getProjectFillStyle(
                  fill.projectId,
                  projectColorById.get(fill.projectId),
                );
                const isUnconfirmed = unconfirmedProjectIds.has(fill.projectId);
                return (
                  <div
                    key={fill.projectId ?? `project-${fill.heightPercentage}`}
                    className={`timeline-cell__fill${isUnconfirmed ? " timeline-cell__fill--unconfirmed" : ""}${fill.unassigned ? " timeline-cell__fill--unassigned" : ""}`}
                    style={{
                      height: `${fill.heightPercentage}%`,
                      bottom: `${fill.bottomPercentage}%`,
                      ...colorStyle,
                    }}
                  />
                );
              })}
              {cell.hasAllocation ? (
                // Hours are shown visually only on hover/while editing (they'd be
                // unreadable drawn on a dark fill). This keeps the value in the
                // DOM for screen readers and non-hover users on read-only rows.
                <span className="sr-only">{cell.totalHours}h allocated</span>
              ) : null}
              {hasOoo ? (
                <div
                  className="timeline-cell__ooo"
                  style={{ height: `${oooHeight}%` }}
                  data-testid="timeline-cell-ooo"
                  aria-label={`Out of office ${oooHours}h`}
                />
              ) : null}
              {isEditingOoo ? (
                <span
                  className="timeline-cell__ooo-editor"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="number"
                    className="timeline-cell__hours-input timeline-cell__ooo-input"
                    value={oooValue}
                    placeholder="0"
                    aria-label="Out of office hours"
                    onChange={(e) => setOooValue(e.target.value)}
                    onBlur={commitOoo}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        cancelOoo();
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    autoFocus
                    min="0"
                    max={MAX_OOO_HOURS}
                    step="1"
                  />
                  <span className="timeline-cell__ooo-unit" aria-hidden>
                    h
                  </span>
                </span>
              ) : null}
              {showLocalEdit && canEdit && editingWeek === cell.key ? (
                <input
                  type="number"
                  className="timeline-cell__hours-input"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onBlur={() => {
                    const numValue = parseFloat(editingValue);
                    if (!isNaN(numValue) && numValue >= 0 && onHoursChange) {
                      onHoursChange(cell.key, numValue, singleCellEdit);
                    }
                    setEditingWeek(null);
                    setEditingValue("");
                    setSingleCellEdit(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    } else if (e.key === "Escape") {
                      setEditingWeek(null);
                      setEditingValue("");
                      setSingleCellEdit(false);
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  autoFocus
                  min="0"
                  step="1"
                />
              ) : canEdit && !cell.hasAllocation ? (
                // Empty editable cell keeps the "+" affordance; allocated cells
                // no longer render hours inline (shown in the hover tooltip).
                <span
                  className="timeline-cell__hours timeline-cell__hours--editable timeline-cell__hours--empty"
                />
              ) : null}
            </div>
          );
        })}
      </div>
      {cellTooltip ? renderCellTooltip(cellTooltip, hoursLabel) : null}
    </div>
  );
}
